import React, { useState } from 'react'
import { Box, Button, ButtonBase, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { AddTarget, TargetHint } from './builderParts'
import type { Condition, CrossState, MergeGroup } from './builderModel'
import { crossSelectionFit, resolveAnswers, setCrossSlot, swapCross, type CrossSlot } from './selectionModel'
import type { Category } from './useBuilderData'

// CrossBuilder makes every combination of two variables' answers a category,
// shown as the grid you'd get tabulating one by the other. The two questions
// are two slots, filled only from the tree: tick a question (all its answers)
// or some of its answers and press a slot's green +, or tick two questions
// and press "Use these two". Columns can be merged (and split again) before
// saving. It shows no respondent counts: those are seen in a table once the
// construct is created.
export interface CrossBuilderProps {
  cross: CrossState | null
  onChange: (cross: CrossState | null) => void
  categoriesOf: (name: string) => Category[] | undefined
  variableLabel: (name: string) => string
  onOpenVariable: (name: string) => Promise<Category[]>
  // The tree's ticked variables and answers (one condition per variable, in
  // tree order).
  selection: Condition[]
  // Called once the selection has been used, to clear the ticks.
  onSelectionUsed: () => void
}

// mergeColumns folds the selected column groups into the first of them.
export const mergeColumns = (groups: MergeGroup[], selected: number[]): MergeGroup[] => {
  if (selected.length < 2) return groups
  const order = [...selected].sort((a, b) => a - b)
  const merged: MergeGroup = {
    label: order.map(i => groups[i].label).join(' & '),
    codes: order.flatMap(i => groups[i].codes),
  }
  return groups.flatMap((g, i) => (i === order[0] ? [merged] : order.includes(i) ? [] : [g]))
}

const SLOT_NAME: Record<CrossSlot, string> = { rows: 'first question', cols: 'second question' }

// SlotCard is one of the two questions: an empty placeholder until filled,
// then the variable's name, its question in grey, the answers used and an x.
const SlotCard: React.FC<{
  slot: CrossSlot
  variable: string
  question: string
  answers: string[]
  onClear: () => void
  target: React.ReactNode
}> = ({ slot, variable, question, answers, onClear, target }) => {
  const title = slot === 'rows' ? 'First question' : 'Second question'
  return (
    <Box
      data-testid={`cross-slot-${slot}`}
      role="group"
      aria-label={title}
      sx={{
        border: '1px solid',
        borderStyle: variable ? 'solid' : 'dashed',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        display: 'grid',
        gap: 0.75,
        alignContent: 'start',
        bgcolor: variable ? 'background.paper' : 'transparent',
        minWidth: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </Typography>
      {variable ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" color="text.primary" noWrap>{variable}</Typography>
              {question !== variable && (
                <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3 }}>
                  {question}
                </Typography>
              )}
            </Box>
            <Tooltip title={`Clear the ${SLOT_NAME[slot]}`}>
              <IconButton size="small" aria-label={`Clear ${SLOT_NAME[slot]}`} onClick={onClear}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" data-testid={`cross-slot-${slot}-answers`}>
            {answers.length} {answers.length === 1 ? 'answer' : 'answers'}: {answers.join(', ')}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Tick a question in the tree (all its answers, or only some), then press the green + here.
        </Typography>
      )}
      {target}
    </Box>
  )
}

export const CrossBuilder: React.FC<CrossBuilderProps> = ({
  cross, onChange, categoriesOf, variableLabel, onOpenVariable, selection, onSelectionUsed,
}) => {
  const [selected, setSelected] = useState<number[]>([])
  const [notice, setNotice] = useState<string | null>(null)
  const rows = cross?.rows || ''
  const cols = cross?.cols || ''
  const fit = crossSelectionFit(selection)

  const answersOf = async (pick: Condition) => {
    const { answers } = await resolveAnswers(pick, categoriesOf(pick.variable), onOpenVariable)
    if (answers.length === 0) setNotice(`${pick.variable} has no answers to cross. Tick a question with answers.`)
    return answers
  }

  // fillSlot fills one slot with the one ticked question.
  const fillSlot = async (slot: CrossSlot) => {
    const pick = selection[0]
    if (!pick) return
    const answers = await answersOf(pick)
    if (answers.length === 0) return
    setNotice(null)
    setSelected([])
    onChange(setCrossSlot(cross, slot, { variable: pick.variable, answers }))
    onSelectionUsed()
  }

  // fillBoth fills both slots with the two ticked questions, in tree order.
  const fillBoth = async () => {
    if (selection.length !== 2) return
    const [first, second] = await Promise.all(selection.map(answersOf))
    if (first.length === 0 || second.length === 0) return
    setNotice(null)
    setSelected([])
    onChange(setCrossSlot(
      setCrossSlot(cross, 'rows', { variable: selection[0].variable, answers: first }),
      'cols', { variable: selection[1].variable, answers: second },
    ))
    onSelectionUsed()
  }

  const clearSlot = (slot: CrossSlot) => {
    setSelected([])
    const next = setCrossSlot(cross, slot, null)
    onChange(next.rows || next.cols ? next : null)
  }

  const labelOf = (variable: string, code: string) => categoriesOf(variable)?.find(c => c.code === code)?.label || code
  const swap = () => {
    if (!cross?.rows || !cross?.cols) return
    setSelected([])
    onChange(swapCross(cross, labelOf))
  }

  const split = (index: number) => {
    if (!cross) return
    const cats = categoriesOf(cross.cols) || []
    const parts = cross.colGroups[index].codes.map(code => ({ label: cats.find(c => c.code === code)?.label || code, codes: [code] }))
    setSelected([])
    onChange({ ...cross, colGroups: cross.colGroups.flatMap((g, i) => (i === index ? parts : [g])) })
  }

  const rowCats = rows ? categoriesOf(rows) : undefined
  const rowLabel = (code: string) => rowCats?.find(c => c.code === code)?.label || cross?.rowLabels[code] || code
  const ready = Boolean(cross?.rows && cross?.cols && cross.rowCodes.length && cross.colGroups.length)

  // Targets are shown only where the selection can go, always green. One
  // ticked question: a slot's "Use for …", except a slot that would cross it
  // with itself (the other slot already holds it; it can still replace
  // itself, to change the answers used). Two: "Use these two". Three or
  // more: no target, one line saying why.
  const slotOpen = (slot: CrossSlot) => fit === 'one' && selection[0].variable !== (slot === 'rows' ? cols : rows)
  const slotTarget = (slot: CrossSlot) => (slotOpen(slot)
    ? <AddTarget label={`Use for ${SLOT_NAME[slot]}`} text={`Use for ${SLOT_NAME[slot]}`} onClick={() => fillSlot(slot)} />
    : null)
  const hint = fit === 'tooMany'
    ? 'Cross works on two questions: tick one question for a slot, or two for both.'
    : fit === 'one' && !slotOpen('rows') && !slotOpen('cols')
      ? 'That question is already in a slot, and a question cannot be crossed with itself.'
      : null

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto minmax(0, 1fr)' }, alignItems: 'center', gap: 1.5 }}>
        <SlotCard
          slot="rows"
          variable={rows}
          question={rows ? variableLabel(rows) : ''}
          answers={(cross?.rowCodes || []).map(rowLabel)}
          onClear={() => clearSlot('rows')}
          target={slotTarget('rows')}
        />
        <Tooltip title="Swap the two questions">
          <span style={{ justifySelf: 'center' }}>
            <IconButton aria-label="Swap the two questions" onClick={swap} disabled={!rows || !cols}>
              <SwapHorizIcon />
            </IconButton>
          </span>
        </Tooltip>
        <SlotCard
          slot="cols"
          variable={cols}
          question={cols ? variableLabel(cols) : ''}
          answers={(cross?.colGroups || []).map(g => g.label)}
          onClear={() => clearSlot('cols')}
          target={slotTarget('cols')}
        />
      </Box>

      {fit === 'two' && (
        <Box sx={{ justifySelf: 'start', border: '1px dashed', borderColor: 'divider', borderRadius: 2, px: 1.5, py: 1 }}>
          <AddTarget label="Use these two" text="Use these two" onClick={fillBoth} />
        </Box>
      )}
      {hint && <TargetHint>{hint}</TargetHint>}
      {notice && <TargetHint>{notice}</TargetHint>}

      {ready && cross && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minHeight: 36 }}>
            {selected.length >= 2 ? (
              <>
                <Typography variant="body2">{selected.length} columns selected</Typography>
                <Button size="small" variant="contained" color="info" onClick={() => { onChange({ ...cross, colGroups: mergeColumns(cross.colGroups, selected) }); setSelected([]) }}>
                  Merge into one column
                </Button>
                <Button size="small" color="info" onClick={() => setSelected([])}>Clear</Button>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {cross.rowCodes.length * cross.colGroups.length} codes. Select column headings to merge small or related columns.
              </Typography>
            )}
          </Box>

          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table size="small" aria-label="Codes this cross will create" sx={{ '& td, & th': { fontVariantNumeric: 'tabular-nums' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'action.hover', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {variableLabel(cross.rows)} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>by</Box> {variableLabel(cross.cols)}
                  </TableCell>
                  {cross.colGroups.map((g, ci) => {
                    const isSel = selected.includes(ci)
                    return (
                      <TableCell key={ci} align="right" sx={{ bgcolor: isSel ? 'info.main' : 'action.hover', color: isSel ? 'info.contrastText' : 'text.primary', p: 0 }}>
                        <ButtonBase
                          aria-pressed={isSel}
                          onClick={() => setSelected(s => (isSel ? s.filter(x => x !== ci) : [...s, ci]))}
                          sx={{ width: '100%', justifyContent: 'flex-end', px: 1.5, py: 1, fontWeight: 600, fontSize: 13 }}
                        >
                          {g.label}
                        </ButtonBase>
                        {g.codes.length > 1 && (
                          <Button size="small" onClick={() => split(ci)} sx={{ mb: 0.5, mr: 1, minWidth: 0, py: 0, color: isSel ? 'inherit' : 'info.main' }}>
                            Split
                          </Button>
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {cross.rowCodes.map(code => (
                  <TableRow key={code}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>{rowLabel(code)}</TableCell>
                    {cross.colGroups.map((g, ci) => (
                      <TableCell key={ci} align="right">
                        <Typography variant="body2" color="text.secondary">{rowLabel(code)} x {g.label}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  )
}

export default CrossBuilder
